import React, { useState, useEffect, useCallback } from 'react';
import './Dashboard.css';

const API_BASE = '/api';

function Dashboard({ user, onLogout }) {
  const [message, setMessage] = useState({ type: '', text: '' });
  const [statusLoading, setStatusLoading] = useState(true);
  const [soundPlaying, setSoundPlaying] = useState(false);
  const [facilitySoundPlaying, setFacilitySoundPlaying] = useState(false);
  const [pushLoading, setPushLoading] = useState(false);
  const [marketKontrolSuresi, setMarketKontrolSuresi] = useState(5000);
  const [marketKontrolSuresiSaving, setMarketKontrolSuresiSaving] = useState(false);
  const [settingsLoading, setSettingsLoading] = useState({});
  const [cacheStatus, setCacheStatus] = useState(null);
  const [serverStartedAt, setServerStartedAt] = useState(null);

  const getToken = () => localStorage.getItem('token');
  const headers = () => ({
    'Content-Type': 'application/json',
    Authorization: `Bearer ${getToken()}`,
  });

  const fetchAccountsStatus = useCallback(async () => {
    setStatusLoading(true);
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_BASE}/nesine/status`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      if (data.success) {
        // status data used for loading indicator only
      }
    } catch {
      console.error('Status check failed');
    }
    setStatusLoading(false);
  }, []);

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (!token) return;

    fetchAccountsStatus();
    fetch(`${API_BASE}/settings`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.json())
      .then(d => {
        if (d.success) {
          if (d.data.marketKontrolSuresi != null) setMarketKontrolSuresi(d.data.marketKontrolSuresi);
          if (d.data.serverStartedAt) setServerStartedAt(d.data.serverStartedAt);
        }
      })
      .catch(() => {});
    refreshCache();
    const cacheInterval = setInterval(refreshCache, 5000);
    const statusInterval = setInterval(fetchAccountsStatus, 10000);
    return () => { clearInterval(cacheInterval); clearInterval(statusInterval); };
  }, [fetchAccountsStatus]);

  const playSlotSound = async () => {
    setSoundPlaying(true);
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_BASE}/nesine/test-sound`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      if (!data.success) throw new Error(data.message || 'Ses çalınamadı');
      setMessage({ type: 'success', text: '🔊 Sunucuda ses çalındı' });
    } catch (err) {
      setMessage({ type: 'error', text: 'Ses çalınamadı: ' + err.message });
    }
    setSoundPlaying(false);
  };

  const playFacilitySound = async () => {
    setFacilitySoundPlaying(true);
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_BASE}/nesine/test-facility-sound`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      if (!data.success) throw new Error(data.message || 'Ses çalınamadı');
      setMessage({ type: 'success', text: '🏭 Facility sesi çalındı' });
    } catch (err) {
      setMessage({ type: 'error', text: 'Facility sesi çalınamadı: ' + err.message });
    }
    setFacilitySoundPlaying(false);
  };

  const stopSound = async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_BASE}/nesine/stop-sound`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      if (!data.success) throw new Error(data.message || 'Ses durdurulamadı');
      setMessage({ type: 'success', text: '🔇 Ses durduruldu' });
      setSoundPlaying(false);
      setFacilitySoundPlaying(false);
    } catch (err) {
      setMessage({ type: 'error', text: 'Ses durdurulamadı: ' + err.message });
    }
  };

  const sendTestPush = async () => {
    setPushLoading(true);
    setMessage({ type: '', text: '' });
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_BASE}/nesine/push/test`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ title: '🎰 Nesine Bot', body: 'Test bildirimi başarıyla gönderildi!' }),
      });
      const data = await res.json();
      if (data.success) {
        setMessage({ type: 'success', text: 'Bildirim gönderildi ✅' });
      } else {
        setMessage({ type: 'error', text: data.message || 'Bildirim gönderilemedi' });
      }
    } catch (err) {
      setMessage({ type: 'error', text: 'Bildirim hatası: ' + err.message });
    }
    setPushLoading(false);
  };

  const refreshCache = () => {
    const token = localStorage.getItem('token');
    if (!token) return;
    fetch(`${API_BASE}/settings/cache`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.json())
      .then(d => { if (d.success) setCacheStatus(d.data); })
      .catch(() => {});
  };

  const toggleSetting = async (key, currentValue) => {
    setSettingsLoading(prev => ({ ...prev, [key]: true }));
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_BASE}/settings/${key}`, {
        method: 'PUT',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ enabled: !currentValue }),
      });
      const data = await res.json();
      if (data.success) {
      }
    } catch (err) {
      console.error(`${key} toggle error:`, err);
    }
    setSettingsLoading(prev => ({ ...prev, [key]: false }));
    refreshCache();
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleString('tr-TR', { timeZone: 'Europe/Istanbul' });
  };

  return (
    <div className="dashboard">
      <div className="dashboard-content">

        {statusLoading && (
          <div className="status-loading">
            <div className="spinner" />
            <p>Durum kontrol ediliyor...</p>
          </div>
        )}

        {/* Message */}
        {message.text && (
          <div className={`alert alert-${message.type}`}>
            {message.text}
            <button className="alert-close" onClick={() => setMessage({ type: '', text: '' })}>×</button>
          </div>
        )}

        {/* Settings Section - always visible */}
        {!statusLoading && (
          <>
            <div className="settings-card">
              <h3>🔧 Hızlı İşlemler</h3>
              <div className="status-actions-row">
                <button
                  onClick={playSlotSound}
                  className="sound-test-btn"
                  disabled={soundPlaying}
                  title="Slot sesini test et"
                >
                  {soundPlaying ? '🔊 Çalıyor...' : '🔔 Zil Test'}
                </button>
                <button
                  onClick={playFacilitySound}
                  className="facility-test-btn"
                  disabled={facilitySoundPlaying}
                  title="Facility sesini test et"
                >
                  {facilitySoundPlaying ? '🔊 Çalıyor...' : '🏭 Facility Test'}
                </button>
                <button
                  onClick={stopSound}
                  className="stop-sound-btn"
                  title="Çalan sesi durdur"
                >
                  ⏹️ Ses Durdur
                </button>
                <button
                  onClick={sendTestPush}
                  className="push-test-btn"
                  disabled={pushLoading}
                  title="Test bildirimi gönder"
                >
                  {pushLoading ? '📤 Gönderiliyor...' : '📱 Bildirim Test'}
                </button>
              </div>
            </div>

            <div className="settings-card">
              <h3>⚙️ Ayarlar</h3>

              <div className="settings-list">
              </div>

              {
                <div className="acik-market-suresi-section">
                  <div className="bet-amount-group">
                    <span className="bet-amount-title">🔍 Market Kontrol Süresi</span>
                    <div className="bet-amount-inputs">
                      <div className="bet-input-item">
                        <label>Milisaniye</label>
                        <input
                          type="number"
                          min="1"
                          max="300000"
                          step="100"
                          value={marketKontrolSuresi}
                          onChange={(e) => setMarketKontrolSuresi(Number(e.target.value))}
                        />
                      </div>
                      <div className="bet-input-item" style={{ justifyContent: 'flex-end' }}>
                        <button
                          className="save-bet-amounts-btn"
                          style={{ marginTop: 0 }}
                          disabled={marketKontrolSuresiSaving}
                          onClick={async () => {
                            setMarketKontrolSuresiSaving(true);
                            try {
                              const token = localStorage.getItem('token');
                              const res = await fetch(`${API_BASE}/settings/marketKontrolSuresi`, {
                                method: 'PUT',
                                headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
                                body: JSON.stringify({ value: marketKontrolSuresi }),
                              });
                              const data = await res.json();
                              if (data.success) {
                                setMarketKontrolSuresi(data.data.marketKontrolSuresi);
                                setMessage({ type: 'success', text: `Market Kontrol Süresi: ${data.data.marketKontrolSuresi}ms ✅` });
                              } else {
                                setMessage({ type: 'error', text: data.message || 'Kaydetme başarısız' });
                              }
                            } catch (err) {
                              setMessage({ type: 'error', text: 'Kaydetme hatası: ' + err.message });
                            }
                            setMarketKontrolSuresiSaving(false);
                            refreshCache();
                          }}
                        >
                          {marketKontrolSuresiSaving ? 'Kaydediliyor...' : '💾 Kaydet'}
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              }

            </div>

            {/* Server Uptime */}
            {serverStartedAt && (
              <div className="settings-card server-uptime" style={{ marginTop: 16 }}>
                {(() => {
                    const started = new Date(serverStartedAt);
                    const now = new Date();
                    const diff = now.getTime() - started.getTime();
                    const d = Math.floor(diff / 86400000);
                    const h = Math.floor((diff % 86400000) / 3600000);
                    const m = Math.floor((diff % 3600000) / 60000);
                    const s = Math.floor((diff % 60000) / 1000);
                    const time = started.toLocaleTimeString('tr-TR', { timeZone: 'Europe/Istanbul', hour: '2-digit', minute: '2-digit', second: '2-digit' });
                    const toIstanbulDate = (dt) => dt.toLocaleDateString('en-CA', { timeZone: 'Europe/Istanbul' });
                    const dayDiff = Math.round((new Date(toIstanbulDate(now)) - new Date(toIstanbulDate(started))) / 86400000);
                    let dateStr;
                    if (dayDiff === 0) dateStr = 'Bugün';
                    else if (dayDiff === 1) dateStr = 'Dün';
                    else if (dayDiff < 7) dateStr = started.toLocaleDateString('tr-TR', { timeZone: 'Europe/Istanbul', weekday: 'long' });
                    else dateStr = started.toLocaleDateString('tr-TR', { timeZone: 'Europe/Istanbul', day: '2-digit', month: '2-digit', year: 'numeric' });
                    const parts = [];
                    if (d > 0) parts.push(`${d}g`);
                    if (h > 0 || d > 0) parts.push(`${h}sa`);
                    parts.push(`${m}dk`);
                    parts.push(`${s}sn`);
                    return (
                      <>
                        <div className="uptime-row">
                          <span className="uptime-dot">●</span>
                          <span className="uptime-label">Sunucu Açık</span>
                          <span className="uptime-duration">{parts.join(' ')}</span>
                        </div>
                        <div className="uptime-date">{dateStr} {time}</div>
                      </>
                    );
                  })()}
              </div>
            )}

          </>
        )}
      </div>
    </div>
  );
}

export default Dashboard;
